import React, { useMemo } from 'react';
import { ButtonGroup } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/glyph/edit';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger';
import type { Priority, ReorderHandler } from '../../../common/types';
import { priorityModalSwitcherEntryPoint } from '../../priority-modals/entrypoint';
import type { PriorityModalProps } from '../../priority-modals/tmp-priority-modal-switcher';
import { messages } from './messages';
import { Reorder } from './reorder';

type ActionsProps = {
	priority: Priority;
	canMoveUp: boolean;
	canMoveDown: boolean;
	shouldFocusMoveButton: boolean;
	onDelete: () => void;
	onEdit: () => void;
	onReorder: ReorderHandler;
	onSelectPosition: (priority: Priority) => void;
	onClosePriorityModal?: () => void;
	onDeleteConfirm?: (priority: Priority) => void;
	onSetPriorityPosition?: (priorityId: string, order: number) => Promise<void>;
	totalItems?: number;
};

export const Actions = ({
	priority,
	canMoveUp,
	canMoveDown,
	shouldFocusMoveButton,
	onDelete,
	onEdit,
	onReorder,
	onSelectPosition,
	onClosePriorityModal,
	onDeleteConfirm,
	onSetPriorityPosition,
	totalItems,
}: ActionsProps) => {
	const { formatMessage } = useIntl();

	const entryPointProps = useMemo(
		() => ({
			onClose: () => onClosePriorityModal && onClosePriorityModal(),
			currentModal: {
				modalName: priority.schemes.total > 0 ? ('DELETE_IN_USE' as const) : ('DELETE' as const),
				priority,
			},
			totalItems,
			onDelete: (itemPriority: Priority) => onDeleteConfirm && onDeleteConfirm(itemPriority),
			onSetPosition: (priorityId: string, order: number) =>
				onSetPriorityPosition && onSetPriorityPosition(priorityId, order),
		}),
		[totalItems, onClosePriorityModal, onSetPriorityPosition, onDeleteConfirm, priority],
	);

	return (
		<ButtonGroup>
			<IconButton
				icon={EditIcon}
				appearance="subtle"
				label={formatMessage(messages.edit, { priorityName: priority.name })}
				onClick={onEdit}
			/>
			{fg('jsw_priority_modals_admin_page') ? (
				<ModalEntryPointPressableTrigger
					entryPoint={priorityModalSwitcherEntryPoint}
					// these assertions will be removed in tidying of jsw_priority_modals_admin_page

					entryPointProps={
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						entryPointProps as
							| (Omit<PriorityModalProps, 'onClose'> & {
									onClose?: (() => void) | undefined;
							  })
							| undefined
					}
					interactionName="priorities-table-delete-modal"
					// Todo add below in cleanup of jsw_priority_modals_admin_page
					// modalProps={modalProps}
					// useInternalModal
				>
					{({ ref }) => (
						<IconButton
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							ref={ref as React.Ref<HTMLButtonElement>}
							icon={TrashIcon}
							appearance="subtle"
							testId="admin-pages-priorities.ui.priorities-table.actions.delete-button"
							label={formatMessage(messages.delete, { priorityName: priority.name })}
							onClick={onDelete}
						/>
					)}
				</ModalEntryPointPressableTrigger>
			) : (
				<IconButton
					icon={TrashIcon}
					appearance="subtle"
					label={formatMessage(messages.delete, { priorityName: priority.name })}
					onClick={onDelete}
				/>
			)}
			<Reorder
				priority={priority}
				canMoveUp={canMoveUp}
				canMoveDown={canMoveDown}
				shouldFocus={shouldFocusMoveButton}
				onReorder={onReorder}
				onSelectPosition={onSelectPosition}
				{...(fg('jsw_priority_modals_admin_page')
					? {
							onClosePriorityModal,
							onDeleteConfirm,
							onSetPriorityPosition,
							totalItems,
						}
					: {})}
			/>
		</ButtonGroup>
	);
};
